"use client";

import { useEffect, useRef, useState } from "react";
import { signIn, signUp } from "@/lib/apex-pass/client";
import { PASS_DISCOUNT_PCT, PASS_PRICE_USD } from "@/lib/apex-pass/offer";

/* Sign in to, or create, an Apex Pass. The same account works in every Apex
 * store and in the Arcade. Creating one is free; membership is separate. */
export function PassPanel({ mode, onMode, onClose }: { mode: "signin" | "signup"; onMode: (m: "signin" | "signup") => void; onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [over13, setOver13] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: "error" | "ok"; text: string } | null>(null);
  const first = useRef<HTMLInputElement>(null);

  useEffect(() => {
    first.current?.focus();
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    addEventListener("keydown", k);
    return () => removeEventListener("keydown", k);
  }, [onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setMsg(null);
    if (mode === "signin") {
      const r = await signIn(email, password);
      setBusy(false);
      if (r.ok) onClose(); else setMsg({ tone: "error", text: r.error });
    } else {
      const r = await signUp(email, password, over13);
      setBusy(false);
      if (!r.ok) setMsg({ tone: "error", text: r.error });
      else if (r.confirm) setMsg({ tone: "ok", text: r.message || "Check your email to confirm your Apex Pass, then sign in." });
      else onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center p-4" role="dialog" aria-modal="true" aria-labelledby="pass-h">
      <button type="button" aria-label="Close" className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md rounded-3xl border border-white/15 bg-[#070c18] p-7 text-white shadow-2xl">
        <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 px-2 text-white/65 hover:text-white">✕</button>
        <p className="font-mono text-[11px] uppercase tracking-[.2em] text-amber-300">Apex Pass</p>
        <h2 id="pass-h" className="mt-2 text-2xl font-semibold tracking-tight">{mode === "signin" ? "Sign in to your Pass" : "Get your free Apex Pass"}</h2>
        <p className="mt-2 text-sm text-white/65">
          One account for every Apex store and the Arcade: orders, downloads, websites and game scores in one place.
          {mode === "signup" ? ` Free. Members add ${PASS_DISCOUNT_PCT}% off digital for $${PASS_PRICE_USD}/yr.` : ""}
        </p>
        <label className="mt-5 block text-sm">
          <span className="text-white/70">Email</span>
          <input ref={first} type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-white outline-none focus:border-amber-300/70" />
        </label>
        <label className="mt-3 block text-sm">
          <span className="text-white/70">Password</span>
          <input type="password" required minLength={mode === "signup" ? 8 : 1} maxLength={72} autoComplete={mode === "signin" ? "current-password" : "new-password"}
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-white outline-none focus:border-amber-300/70" />
          {mode === "signup" && <span className="mt-1 block text-xs text-white/65">8 to 72 characters.</span>}
        </label>
        {mode === "signup" && (
          <label className="mt-3 flex items-start gap-2 text-sm text-white/75">
            <input type="checkbox" checked={over13} onChange={(e) => setOver13(e.target.checked)} required className="mt-1" />
            I am 13 or older. (Younger players can keep playing the Arcade as guests.)
          </label>
        )}
        {msg && <p role="alert" className={`mt-4 rounded-xl px-4 py-3 text-sm ${msg.tone === "error" ? "bg-yellow-500/15 text-yellow-200" : "bg-emerald-500/15 text-emerald-200"}`}>{msg.text}</p>}
        <button type="submit" disabled={busy} className="mt-5 w-full rounded-xl bg-white py-3.5 font-semibold text-[#0b0806] hover:bg-amber-100 disabled:opacity-60">
          {busy ? "One moment…" : mode === "signin" ? "Sign in" : "Create my free Pass"}
        </button>
        <p className="mt-4 text-center text-sm text-white/65">
          {mode === "signin" ? "New here? " : "Already have one? "}
          <button type="button" onClick={() => { setMsg(null); onMode(mode === "signin" ? "signup" : "signin"); }} className="font-semibold text-amber-300 hover:text-white">
            {mode === "signin" ? "Get a free Apex Pass" : "Sign in"}
          </button>
        </p>
      </form>
    </div>
  );
}
